import { useState, useRef } from 'react';

const CONCEPT_TYPES = [
  'Fine Dining', 'Casual Dining', 'Fast Casual', 'Quick Service',
  'Bar / Lounge', 'Cocktail Bar', 'Wine Bar', 'Brewery / Taproom',
  'Cafe / Coffee Shop', 'Bakery', 'Hotel F&B', 'Resort F&B',
  'Nightclub', 'Rooftop', 'Food Hall', 'Ghost Kitchen', 'Catering', 'Other',
];

const STEPS = [
  { key: 'companyTag', label: 'Client / Company', placeholder: 'e.g., Coury Hospitality', required: false, type: 'text' },
  { key: 'conceptName', label: 'Concept Name', placeholder: 'e.g., The Golden Fork', required: true, type: 'text' },
  { key: 'conceptType', label: 'Concept Type', required: true, type: 'select', options: CONCEPT_TYPES },
  { key: 'city', label: 'City / Market', placeholder: 'e.g., Miami, FL', required: true, type: 'text' },
  { key: 'website', label: 'Website URL', placeholder: 'https://...', required: false, type: 'url' },
  { key: 'instagram', label: 'Instagram Handle', placeholder: '@thegoldenfork', required: false, type: 'text' },
  { key: 'financials', label: 'Financial Snapshot', sublabel: 'Upload P&L, budget, or financial summary', required: false, type: 'file', accept: '.pdf,.xlsx,.xls,.csv,.doc,.docx', textKey: 'financialSnapshot', textPlaceholder: 'Or paste key financial data here...' },
  { key: 'menu', label: 'Menu', sublabel: 'Upload menu PDF, photos, or describe it', required: false, type: 'file', accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx', textKey: 'menuNotes', textPlaceholder: 'Or paste/describe the menu here...' },
  { key: 'deck', label: 'Pitch Deck / Concept Docs', sublabel: 'Upload any supporting presentations or documents', required: false, type: 'file', accept: '.pdf,.pptx,.ppt,.doc,.docx', textKey: 'deckNotes', textPlaceholder: 'Or paste key points here...' },
  { key: 'additionalNotes', label: 'Additional Notes', placeholder: 'Anything else we should know...', required: false, type: 'textarea' },
];

export default function IntakeForm({ onComplete, existingAudit = null }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    conceptName: existingAudit?.intake?.conceptName || '',
    conceptType: existingAudit?.intake?.conceptType || '',
    city: existingAudit?.intake?.city || '',
    website: existingAudit?.intake?.website || '',
    instagram: existingAudit?.intake?.instagram || '',
    financialSnapshot: existingAudit?.intake?.financialSnapshot || '',
    menuNotes: existingAudit?.intake?.menuNotes || '',
    deckNotes: existingAudit?.intake?.deckNotes || '',
    additionalNotes: existingAudit?.intake?.additionalNotes || '',
  });
  const [files, setFiles] = useState({
    financials: null,
    menu: null,
    deck: null,
  });
  const fileInputRef = useRef(null);

  const isReAudit = !!existingAudit;
  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const canProceed = !currentStep.required || (
    currentStep.type === 'file'
      ? (files[currentStep.key] || formData[currentStep.textKey]?.trim())
      : currentStep.type === 'select'
        ? !!formData[currentStep.key]
        : formData[currentStep.key]?.trim()
  );

  const handleNext = () => {
    if (isLast) {
      onComplete({ ...formData, files });
    } else {
      setStep(step + 1);
    }
  };

  const handleSkip = () => {
    if (isLast) {
      onComplete({ ...formData, files });
    } else {
      setStep(step + 1);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [currentStep.key]: file });
    }
  };

  const removeFile = () => {
    setFiles({ ...files, [currentStep.key]: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="max-w-lg mx-auto">
      {/* Re-audit badge */}
      {isReAudit && (
        <div className="mb-6 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-4 py-3 text-center">
          <span className="text-brand-gold text-sm font-medium">
            Re-Audit Mode — Comparing against previous assessment
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-neutral-500 mb-2">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-neutral-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-gold rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="animate-fade-in-up" key={step}>
        <label className="block text-lg font-semibold text-white mb-1">
          {currentStep.label}
          {currentStep.required && <span className="text-brand-gold ml-1">*</span>}
        </label>
        {currentStep.sublabel && (
          <p className="text-xs text-neutral-400 mb-3">{currentStep.sublabel}</p>
        )}
        {!currentStep.required && !currentStep.sublabel && (
          <p className="text-xs text-neutral-500 mb-3">Optional — skip if you don't have this</p>
        )}

        {currentStep.type === 'select' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
            {currentStep.options.map(opt => (
              <button
                key={opt}
                onClick={() => setFormData({ ...formData, [currentStep.key]: opt })}
                className={`text-left p-3 rounded-xl border-2 transition-all text-sm ${
                  formData[currentStep.key] === opt
                    ? 'border-brand-gold bg-brand-gold/10 text-brand-gold font-medium'
                    : 'border-neutral-800 hover:border-neutral-600 text-neutral-400'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        ) : currentStep.type === 'file' ? (
          <div className="space-y-4 mt-2">
            {/* File upload area */}
            <div
              className="border-2 border-dashed border-neutral-700 rounded-xl p-6 text-center hover:border-brand-gold/40 transition-all cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {files[currentStep.key] ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center">
                    <span className="text-brand-gold text-sm">📄</span>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-medium">{files[currentStep.key].name}</p>
                    <p className="text-neutral-500 text-xs">
                      {(files[currentStep.key].size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    className="text-neutral-500 hover:text-red-400 text-sm ml-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-neutral-800 flex items-center justify-center mx-auto mb-3">
                    <span className="text-neutral-400 text-xl">↑</span>
                  </div>
                  <p className="text-neutral-300 text-sm mb-1">Click to upload or drag and drop</p>
                  <p className="text-neutral-600 text-xs">{currentStep.accept.replace(/\./g, '').toUpperCase()}</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={currentStep.accept}
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Text alternative */}
            <div className="flex items-center gap-3">
              <div className="h-[1px] flex-1 bg-neutral-800"></div>
              <span className="text-neutral-600 text-xs uppercase tracking-wider">or type it</span>
              <div className="h-[1px] flex-1 bg-neutral-800"></div>
            </div>

            <textarea
              value={formData[currentStep.textKey] || ''}
              onChange={(e) => setFormData({ ...formData, [currentStep.textKey]: e.target.value })}
              placeholder={currentStep.textPlaceholder}
              rows={3}
              className="input-field text-sm"
            />
          </div>
        ) : currentStep.type === 'textarea' ? (
          <textarea
            value={formData[currentStep.key]}
            onChange={(e) => setFormData({ ...formData, [currentStep.key]: e.target.value })}
            placeholder={currentStep.placeholder}
            rows={4}
            className="input-field text-base mt-2"
            autoFocus
          />
        ) : (
          <input
            type={currentStep.type}
            value={formData[currentStep.key]}
            onChange={(e) => setFormData({ ...formData, [currentStep.key]: e.target.value })}
            placeholder={currentStep.placeholder}
            className="input-field text-base mt-2"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && canProceed && handleNext()}
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mt-8">
        <button
          onClick={() => step > 0 && setStep(step - 1)}
          className={`btn-ghost ${step === 0 ? 'invisible' : ''}`}
        >
          Back
        </button>

        <div className="flex gap-3">
          {!currentStep.required && (
            <button onClick={handleSkip} className="btn-ghost">
              Skip
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLast ? (isReAudit ? 'Start Re-Audit' : 'Start Research') : 'Continue'}
          </button>
        </div>
      </div>

      {/* Quick dots */}
      <div className="flex justify-center gap-1.5 mt-8">
        {STEPS.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (i <= step) setStep(i);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              i === step ? 'bg-brand-gold w-6' : i < step ? 'bg-brand-gold/40' : 'bg-neutral-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
